import { getAuth, onAuthStateChanged } from "firebase/auth";
import { onMounted, onUnmounted } from "vue";
import { clarityService } from "../services/clarity";

/**
 * Composable for Clarity analytics integration
 * Handles authentication state changes and user tracking
 */
export function useClarity() {
    let unsubscribeAuth: (() => void) | null = null;

    /**
     * Initialize Clarity with current user if available
     */
    function initializeClarity() {
        const user = getAuth().currentUser;
        if (user) {
            clarityService.initializeWithUser(user);
        }
    }

    /**
     * Handle authentication state changes
     */
    function setupAuthListener() {
        unsubscribeAuth = onAuthStateChanged(getAuth(), async (user) => {
            if (user) {
                // User signed in - initialize Clarity with user data
                await clarityService.initializeWithUser(user);
            } else {
                // User signed out - clear Clarity user data
                clarityService.clearUser();
            }
        });
    }

    /**
     * Track custom events
     */
    function trackEvent(eventName: string, properties?: Record<string, any>) {
        clarityService.trackEvent(eventName, properties);
    }

    /**
     * Set custom tags
     */
    function setCustomTag(key: string, value: string) {
        clarityService.setCustomTag(key, value);
    }

    onMounted(() => {
        // Initialize Clarity immediately if user is already logged in
        initializeClarity();

        // Set up auth state listener for future changes
        setupAuthListener();
    });

    onUnmounted(() => {
        // Clean up auth listener
        if (unsubscribeAuth) {
            unsubscribeAuth();
        }
    });

    return {
        trackEvent,
        setCustomTag,
    };
}
